import React, { useState } from 'react';

import { Menu, Dropdown, DatePicker } from 'antd';

import styled from 'styled-components';

import { getMoment, getNow } from '@libs/NwMoment';

import { NwButton } from '@components/Gui/NwButton';
import NwIcon from '@components/Gui/NwIcon';
import { StyledGroupButton } from '@components/Gui/StyledGroupButton';
import ContactSocials from '@components/Social/ContactSocials';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const { RangePicker } = DatePicker;

const Container = styled.div`
    display: flex;

    .ant-btn {
        border-color: rgba(0, 0, 0, 0.25) !important;
    }
    .ant-btn:hover,
    .ant-btn:focus {
        border-color: rgba(0, 0, 0, 0.25) !important;
        box-shadow: none;
        &::after {
            display: none;
        }
    }
    .ant-picker {
        border-radius: 0;
        border-color: rgba(0, 0, 0, 0.25) !important;
        &:hover,
        &:focus,
        &:active {
            border-color: rgba(0, 0, 0, 0.25) !important;
        }
        .ant-picker-suffix {
            display: none;
        }
        input {
            text-align: center;
        }
    }
    .ant-picker-focused {
        border-color: rgba(0, 0, 0, 0.25) !important;
        box-shadow: none;
    }
    .ant-picker-range {
        padding: 0;
    }
`;

const IconContainer = styled.div`
    width: 1.5rem;
`;

const ContactTypeContainer = styled.div`
    display: flex;
    align-items: center;
    font-weight: ${(props) => props.active && 'bold'};
`;

const PERIOD_OPTIONS = [
    { label: 'Always', value: 'always' },
    { label: 'Year', value: 'year' },
    { label: 'Month', value: 'month' },
    { label: 'Period', value: 'period' },
];

const OptionButtonStyle = (periodMode) => {
    return {
        // padding: '0 8px',
        fontSize: 16,
        display: 'flex',
        alignItems: 'center',
        borderRadius: periodMode ? '0 6px 6px 0' : 0,
        color: 'rgba(0,0,0,0.25)',
        borderLeft: 'none',
        padding: '0 6px',
        // borderRadius: !periodMode && 0,
    };
};

const PeriodSelector = ({
    defaultOption,
    period,
    onChange,
    onlyDefaultOption,
    alwaysEnabled,
    onChangePeriodOption,
}) => {
    const [option, setOption] = useState(defaultOption || 'year');
    const [openRangePicker, setOpenRangePicker] = useState(false);

    const periodOptions = alwaysEnabled ? PERIOD_OPTIONS : PERIOD_OPTIONS.filter((p) => p.value !== 'always');

    const handleChangeOption = (value) => () => {
        if (value === 'always') {
            onChange([getMoment('1900-01-01'), getNow().endOf('year')]);
        } else {
            if (value === 'year' || value === 'month') {
                if (getMoment(period[0]).year() === 1900) {
                    //if period was 'always, set to current year
                    onChange([getNow().startOf(value), getNow().endOf(value)]);
                } else {
                    //if period was not 'always', set to current period, according to the option
                    onChange([getMoment(period[0]).startOf(value), getMoment(period[0]).endOf(value)]);
                }
            } else {
                onChange([getNow().startOf('year'), getNow().endOf('year')]);
            }
        }
        setOption(value);
        if (onChangePeriodOption) {
            onChangePeriodOption(value);
        }
    };

    const menuItems = periodOptions.map((periodOption) => {
        return {
            key: periodOption.value,
            onClick: handleChangeOption(periodOption.value),
            label: (
                <ContactTypeContainer active={periodOption.value === option}>
                    <IconContainer>
                        {periodOption.value === option && <NwIcon icon={byPrefixAndName.fasl['check']} />}
                    </IconContainer>
                    <div>{periodOption.label}</div>
                </ContactTypeContainer>
            ),
        };
    });

    const handleMovePrev = () => {
        const prevDate = getMoment(period[0]).subtract(1, option);
        onChange([getMoment(prevDate).startOf(option), getMoment(prevDate).endOf(option)]);
    };

    const handleMoveNext = () => {
        const nextDate = getMoment(period[0]).add(1, option);
        onChange([getMoment(nextDate).startOf(option), getMoment(nextDate).endOf(option)]);
    };

    const handleChangeDate = (date) => {
        onChange([getMoment(date).startOf(option), getMoment(date).endOf(option)]);
    };

    return (
        <Container>
            {option === 'period' ? (
                <StyledGroupButton>
                    <NwButton icon={byPrefixAndName.fasl['calendar']} onClick={() => setOpenRangePicker(true)} />
                    <RangePicker
                        autoComplete="off"
                        allowClear={false}
                        format="MMM DD YYYY"
                        value={period}
                        open={openRangePicker}
                        onChange={onChange}
                        separator="~"
                        onOpenChange={setOpenRangePicker}
                        inputReadOnly
                        style={{ height: '28px' }}
                    />
                    {!onlyDefaultOption && (
                        <Dropdown placement="bottomRight" menu={{ items: menuItems }} trigger={['click']}>
                            <div className="ant-dropdown-link" onClick={(e) => e.preventDefault()}>
                                <NwButton icon={byPrefixAndName.fasl['ellipsis-h']} style={OptionButtonStyle(true)} />
                            </div>
                        </Dropdown>
                    )}
                </StyledGroupButton>
            ) : (
                <StyledGroupButton>
                    {option !== 'always' && (
                        <NwButton icon={byPrefixAndName.fasl['chevron-left']} onClick={handleMovePrev} />
                    )}
                    {option !== 'always' ? (
                        <DatePicker
                            autoComplete="off"
                            picker={option}
                            value={period[0]}
                            format={option === 'month' && 'MMM YYYY'}
                            onChange={handleChangeDate}
                            allowClear={false}
                            inputReadOnly
                            style={{ height: '28px' }}
                        />
                    ) : (
                        <NwButton label="Always" />
                    )}
                    {!onlyDefaultOption && (
                        <Dropdown placement="bottomRight" menu={{ items: menuItems }} trigger={['click']}>
                            <div className="ant-dropdown-link" onClick={(e) => e.preventDefault()}>
                                <NwButton icon={byPrefixAndName.fasl['ellipsis-h']} style={OptionButtonStyle()} />
                            </div>
                        </Dropdown>
                    )}
                    {option !== 'always' && (
                        <NwButton icon={byPrefixAndName.fasl['chevron-right']} onClick={handleMoveNext} />
                    )}
                </StyledGroupButton>
            )}
        </Container>
    );
};

export default PeriodSelector;
